import { Link } from "@/components/LLink";
import { useEffect, useState } from "react";
import axios from "axios";
import { ArrowRight, ArrowUpRight, Award, Car, Clock, Columns3, Factory, Grid2x2, Ruler, Shield, ShieldCheck, Sofa, Sun, Timer, TreePine, Truck, Warehouse, Wrench } from "lucide-react";
import { PRODUCTS as RO_PRODUCTS } from "@/data/site";
import ContactForm from "@/components/ContactForm";
import QuoteWizard from "@/components/QuoteWizard";
import { PRODUCT_CONTENT } from "@/pages/ProductPage";
import SEO from "@/components/SEO";
import { Reveal } from "@/components/motion";
import { CONTACT } from "@/data/site";
import { useLang, useSite } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SectionTitle = ({ title, link, linkLabel, testid }) => (
  <div className="flex items-end justify-between border-b border-ink/10 pb-6">
    <h2 className="font-heading text-3xl font-semibold leading-none text-ink md:text-4xl">{title}</h2>
    {link && (
      <Link to={link} data-testid={testid} className="flex items-center gap-2 text-[14px] font-semibold text-ink transition-colors hover:text-gold">
        {linkLabel} <ArrowRight className="h-4 w-4" />
      </Link>
    )}
  </div>
);

function Hero() {
  const { t } = useLang();
  return (
    <section className="relative" data-testid="hero">
      <div className="relative flex min-h-[78vh] items-center overflow-hidden pt-[88px]">
        <img src="/images/hero.jpg" alt="Atelier HEAVY METAL" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/55 to-black/30" />
        <div className="relative z-10 mx-auto grid w-full max-w-7xl items-center gap-14 px-4 pb-32 pt-20 sm:px-6 lg:grid-cols-[1.15fr_minmax(360px,430px)] lg:px-8">
          <div>
            <p className="text-[12px] font-semibold uppercase tracking-[0.25em] text-white/70" data-testid="hero-kicker">{t("hero_kicker")}</p>
            <h1 className="mt-6 max-w-3xl font-heading text-4xl font-semibold leading-[1.1] text-white sm:text-5xl lg:text-6xl" data-testid="hero-title">
              {t("hero_title1")} {t("hero_title2a")}
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-white/80 md:text-lg" data-testid="hero-subtitle">
              {t("hero_sub")}
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <Link to="/contact" data-testid="hero-cta-oferta" className="flex items-center gap-2 bg-white px-6 py-3.5 text-[14px] font-semibold text-ink transition-colors duration-300 hover:bg-gold hover:text-base">
                {t("nav_cta")} <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/productie" data-testid="hero-cta-productie" className="flex items-center gap-2 border border-white/40 px-6 py-3.5 text-[14px] font-semibold text-white transition-colors duration-300 hover:border-white hover:bg-white/10">
                {t("hero_cta2")}
              </Link>
            </div>
          </div>
          <QuoteWizard />
        </div>
      </div>
    </section>
  );
}

function Services() {
  const { t } = useLang();
  const { services: SERVICES } = useSite();
  return (
    <section className="relative z-10 -mt-20" data-testid="services-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="border border-ink/10 bg-surface p-3 shadow-2xl shadow-black/15">
          <div className="grid gap-px border border-ink/10 bg-ink/10 sm:grid-cols-2 lg:grid-cols-4">
          {SERVICES.map((s) => (
            <Link key={s.slug} to={`/servicii/${s.slug}`} data-testid={`hero-card-${s.slug}`} className="group flex flex-col bg-base">
              <div className="aspect-[16/10] overflow-hidden">
                <img src={s.image} alt={s.name} className="h-full w-full object-cover transition-opacity duration-300 group-hover:opacity-90" />
              </div>
              <div className="flex flex-1 flex-col p-6">
                <h3 className="font-heading text-lg font-semibold text-ink">{s.name}</h3>
                <p className="mt-2 flex-1 text-[15px] leading-relaxed text-ink/65">{s.short}</p>
                <span className="mt-5 flex items-center gap-1.5 text-[14px] font-semibold text-ink transition-colors group-hover:text-gold">
                  {t("card_details")} <ArrowRight className="h-4 w-4" />
                </span>
              </div>
            </Link>
          ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function Stats() {
  const { t } = useLang();
  const STATS = [
    { value: "±0,5", unit: " mm", label: t("stat_precision"), icon: Ruler },
    { value: "4", unit: ` ${t("tech")}`, label: t("stat_flux"), icon: Factory },
    { value: "24", unit: "h", label: t("stat_offer"), icon: Timer },
    { value: "100", unit: "%", label: t("stat_quality"), icon: Award },
  ];
  return (
    <section className="border-b border-ink/10" data-testid="stats-section">
      <div className="mx-auto grid max-w-7xl grid-cols-2 gap-y-8 px-4 py-14 sm:px-6 lg:grid-cols-4 lg:px-8">
        {STATS.map((s) => (
          <div key={s.label} className="flex min-w-0 items-start gap-4">
            <s.icon className="mt-1 h-6 w-6 shrink-0 text-gold" />
            <div className="min-w-0">
              <p className="font-heading text-2xl font-semibold leading-none text-ink sm:text-3xl">
                {s.value}<span className="text-base text-ink/50 sm:text-3xl">{s.unit}</span>
              </p>
              <p className="mt-2 break-words text-[11px] font-semibold uppercase tracking-[0.12em] text-ink/50 sm:text-[12px] sm:tracking-[0.15em]">{s.label}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

const PRODUCT_ICONS = {
  "mobilier-metalic": Sofa, "porti-garduri": Shield, "carport": Car, "balustrade": Columns3,
  "panouri-decorative": Grid2x2, "terase": Sun, "structuri-metalice": Warehouse, "mobilier-urban": TreePine,
};

function ProductCard({ p, mobile = false }) {
  const { t } = useLang();
  const Icon = PRODUCT_ICONS[p.slug] || Grid2x2;
  const specs = (p.specs || PRODUCT_CONTENT[p.slug]?.specs || []).slice(0, 3);
  const quote = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const roName = RO_PRODUCTS.find((x) => x.slug === p.slug)?.name || p.name;
    window.dispatchEvent(new CustomEvent("hm:quote", { detail: { subject: roName, label: p.name } }));
  };
  return (
    <Link
      to={`/produse/${p.slug}`}
      data-testid={`product-card-${mobile ? "m-" : ""}${p.slug}`}
      className={`group relative block min-w-0 flex-1 overflow-hidden rounded-md ${mobile ? "h-auto" : "h-64 lg:h-72"} bg-[#0b1220] shadow-md transition-[flex-grow,height,box-shadow] duration-500 ease-out hover:z-10 hover:flex-[2.6] lg:hover:h-[340px] hover:shadow-2xl hover:shadow-black/40`}
    >
      <img src={p.image} alt={p.name} className="absolute inset-0 h-full w-full object-cover opacity-80 transition-opacity duration-500 group-hover:opacity-35" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0b1220]/95 via-[#0b1220]/45 to-[#0b1220]/10" />
      <span className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-md border border-white/20 bg-white/10 text-white backdrop-blur transition-colors duration-300 group-hover:bg-white group-hover:text-ink">
        <ArrowUpRight className="h-4 w-4" />
      </span>
      <div className={mobile ? "relative p-5 pt-44" : "absolute inset-x-0 bottom-0 p-5"}>
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gold/90 text-white">
            <Icon className="h-4 w-4" />
          </span>
          <h3 className="truncate font-heading text-[17px] font-semibold text-white">{p.name}</h3>
        </div>
        <p className="mt-2 truncate text-[13px] text-white/75">{p.short}</p>
        <div className={mobile ? "" : "max-h-0 overflow-hidden opacity-0 transition-[max-height,opacity] duration-500 ease-out group-hover:max-h-60 group-hover:opacity-100"}>
          <div className="mt-3 border-t border-white/15 pt-3">
            <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-white/55">{t("home_exec")}</p>
            <ul className="mt-2 space-y-1">
              {specs.map((x) => (
                <li key={x} className="flex items-center gap-2 text-[13px] text-white/90">
                  <span className="h-1.5 w-1.5 rounded-full bg-gold" aria-hidden /> {x}
                </li>
              ))}
            </ul>
            <button
              type="button"
              onClick={quote}
              data-testid={`product-quote-${mobile ? "m-" : ""}${p.slug}`}
              className="mt-4 inline-flex items-center gap-2 bg-white px-4 py-2.5 text-[12px] font-semibold text-ink transition-colors hover:bg-gold hover:text-base"
            >
              {t("card_quote", p.name)} <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </div>
    </Link>
  );
}

const chunk = (arr, n) => arr.reduce((rows, x, i) => (i % n ? rows[rows.length - 1].push(x) : rows.push([x]), rows), []);

function Products() {
  const { t } = useLang();
  const { products: PRODUCTS } = useSite();
  return (
    <section className="border-b border-ink/10 bg-surface" data-testid="products-section">
      <div className="px-4 py-24 sm:px-6 lg:px-10 2xl:px-16">
        <div className="flex flex-col gap-6 border-b border-ink/15 pb-8 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="kicker" data-testid="products-kicker">{t("nav_products")}</p>
            <h2 className="mt-4 font-heading text-3xl font-semibold tracking-tight text-ink md:text-[42px]" data-testid="products-title">{t("build_title")}</h2>
          </div>
          <div className="flex flex-col gap-4 lg:max-w-md lg:items-end lg:text-right">
            <p className="text-[15px] leading-relaxed text-ink/65" data-testid="products-positioning">{t("build_sub")}</p>
            <Link to="/productie" data-testid="products-all-link" className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-ink underline-offset-4 transition-colors hover:text-gold hover:underline">
              {t("products_all").replace(" ↗", "")} <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:hidden">
          {PRODUCTS.map((p) => <ProductCard key={p.slug} p={p} mobile />)}
        </div>
        <div className="mt-10 hidden flex-col gap-4 lg:flex">
          {chunk(PRODUCTS, 4).map((row, i) => (
            <div key={i} className="flex min-h-[340px] items-center gap-4">
              {row.map((p) => <ProductCard key={p.slug} p={p} />)}
            </div>
          ))}
        </div>
        <div className="mt-10 grid gap-px border border-ink/10 bg-ink/10 sm:grid-cols-2 lg:grid-cols-4" data-testid="trust-bar">
          {[Clock, Wrench, ShieldCheck, Truck].map((Icon, i) => (
            <div key={i} className="flex items-center gap-4 bg-base px-6 py-5" data-testid={`trust-item-${i}`}>
              <Icon className="h-5 w-5 shrink-0 text-gold" strokeWidth={1.75} />
              <div>
                <p className="text-[14px] font-semibold text-ink">{t("trust")[i][0]}</p>
                <p className="text-[12px] text-ink/55">{t("trust")[i][1]}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-4 flex flex-col gap-4 border border-ink/10 bg-base p-6 md:flex-row md:items-center md:justify-between" data-testid="product-card-custom">
          <div>
            <h3 className="font-heading text-lg font-semibold text-ink">{t("custom_title")}</h3>
            <p className="mt-1 text-[15px] text-ink/65">{t("custom_text")}</p>
          </div>
          <Link to="/contact" className="flex shrink-0 items-center gap-2 bg-ink px-6 py-3.5 text-[14px] font-semibold text-base transition-colors hover:bg-gold">
            {t("nav_cta")} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}

const WIDE = "px-4 py-24 sm:px-6 lg:px-10 2xl:px-16";

function WideHeader({ kicker, title, text, link, linkLabel, testid, dark = false }) {
  return (
    <div className={`flex flex-col gap-6 border-b pb-8 lg:flex-row lg:items-end lg:justify-between ${dark ? "border-white/15" : "border-ink/15"}`}>
      <div>
        <p className={`kicker ${dark ? "text-white/60" : ""}`}>{kicker}</p>
        <h2 className={`mt-4 font-heading text-3xl font-semibold tracking-tight md:text-[42px] ${dark ? "text-white" : "text-ink"}`} data-testid={`${testid}-title`}>{title}</h2>
      </div>
      {(text || link) && (
        <div className="flex flex-col gap-4 lg:max-w-md lg:items-end lg:text-right">
          {text && <p className={`text-[15px] leading-relaxed ${dark ? "text-white/65" : "text-ink/65"}`}>{text}</p>}
          {link && (
            <Link to={link} data-testid={`${testid}-cta`} className={`inline-flex items-center gap-1.5 text-[13px] font-semibold underline-offset-4 transition-colors hover:text-gold hover:underline ${dark ? "text-white" : "text-ink"}`}>
              {linkLabel} <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          )}
        </div>
      )}
    </div>
  );
}

function Why() {
  const { t } = useLang();
  const CHAPTERS = [
    { nr: "01", title: t("ch1_t"), text: t("ch1_x") },
    { nr: "02", title: t("ch2_t"), text: t("ch2_x") },
    { nr: "03", title: t("ch3_t"), text: t("ch3_x") },
  ];
  return (
    <section className="relative overflow-hidden bg-[#0b1220] text-white" data-testid="manifesto-section">
      <img src="/images/hero.jpg" alt="" aria-hidden className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-[0.14]" loading="lazy" />
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-[#0b1220] via-[#0b1220]/80 to-[#0b1220]/40" />
      <div className={`relative ${WIDE}`}>
        <WideHeader dark kicker={t("manifesto_kicker")} title={<>{t("manifesto_a")} <span className="text-gold">{t("manifesto_b")}</span></>} text={t("why_sub")} testid="manifesto" />
        <div className="mt-12 grid gap-px bg-white/10 md:grid-cols-3" data-testid="manifesto-chapters">
          {CHAPTERS.map((c, i) => (
            <Reveal key={c.nr} delay={i * 0.1} className="bg-[#0b1220]">
              <div className="group relative h-full p-8 lg:p-10">
                <span className="font-heading text-5xl font-semibold leading-none text-white/15 transition-colors duration-500 group-hover:text-gold lg:text-6xl">{c.nr}</span>
                <h3 className="mt-10 font-heading text-xl font-semibold text-white lg:text-2xl">{c.title}</h3>
                <p className="mt-4 text-[15px] leading-relaxed text-white/65">{c.text}</p>
                <span className="absolute inset-x-8 bottom-0 h-px origin-left scale-x-0 bg-gold transition-transform duration-500 group-hover:scale-x-100 lg:inset-x-10" aria-hidden />
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Realizations() {
  const { t } = useLang();
  const [items, setItems] = useState(null);
  useEffect(() => {
    axios.get(`${API}/portfolio`).then(({ data }) => setItems(data.slice(0, 5))).catch(() => setItems([]));
  }, []);
  if (!items || items.length === 0) return null;
  return (
    <section className="border-b border-ink/10 bg-surface" data-testid="realizations-section">
      <div className={WIDE}>
        <WideHeader kicker={t("nav_portfolio")} title={t("pf_title")} text={t("pf_sub")} link="/portofoliu" linkLabel={t("real_cta").replace(" ↗", "")} testid="realizations" />
        <div className={`mt-10 grid gap-4 sm:grid-cols-2 ${items.length >= 4 ? "lg:grid-cols-4 lg:grid-rows-2" : items.length === 3 ? "lg:grid-cols-3" : ""}`} data-testid="realizations-grid">
          {items.map((item, i) => (
            <Link
              key={item.id}
              to="/portofoliu"
              data-testid={i === 0 ? `realization-main-${item.id}` : `realization-side-${item.id}`}
              className={`group relative block overflow-hidden bg-[#0b1220] ${items.length >= 4 && i === 0 ? "sm:col-span-2 sm:row-span-2 min-h-[320px] lg:min-h-0" : items.length < 3 ? "min-h-[360px] lg:min-h-[480px]" : "min-h-[260px] lg:min-h-[320px]"}`}
            >
              <img src={`${API}/files/${item.storage_path}`} alt={item.title} className="absolute inset-0 h-full w-full object-cover opacity-90 transition-[transform,opacity] duration-700 ease-out group-hover:scale-[1.04] group-hover:opacity-60" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0b1220]/90 via-[#0b1220]/20 to-transparent" />
              <span className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center border border-white/25 bg-white/10 text-white backdrop-blur transition-colors duration-300 group-hover:bg-white group-hover:text-ink">
                <ArrowUpRight className="h-4 w-4" />
              </span>
              <div className="absolute inset-x-0 bottom-0 p-6 lg:p-7">
                <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-white/70">
                  {item.category}{item.location ? ` — ${item.location}` : ""}
                </p>
                <h3 className={`mt-2 font-heading font-semibold text-white ${i === 0 ? "text-2xl lg:text-3xl" : "text-lg"}`}>{item.title}</h3>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function PartnerPanel({ image, kicker, title, text, href, linkLabel, testid }) {
  return (
    <Link to={href} data-testid={testid} className="group relative block min-h-[440px] overflow-hidden bg-[#0b1220] lg:min-h-[560px]">
      <img src={image} alt="" aria-hidden className="absolute inset-0 h-full w-full object-cover opacity-70 transition-[transform,opacity] duration-700 ease-out group-hover:scale-[1.04] group-hover:opacity-45" loading="lazy" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0b1220] via-[#0b1220]/55 to-[#0b1220]/10" />
      <span className="absolute right-6 top-6 flex h-11 w-11 items-center justify-center border border-white/25 bg-white/10 text-white backdrop-blur transition-colors duration-300 group-hover:bg-white group-hover:text-ink">
        <ArrowUpRight className="h-5 w-5" />
      </span>
      <div className="absolute inset-x-0 bottom-0 p-8 lg:p-10">
        <p className="text-[11px] font-semibold uppercase tracking-[0.25em] text-white/70">{kicker}</p>
        <h3 className="mt-3 max-w-md font-heading text-2xl font-semibold leading-tight text-white lg:text-3xl">{title}</h3>
        <p className="mt-4 max-w-md text-[15px] leading-relaxed text-white/70">{text}</p>
        <span className="mt-6 inline-flex items-center gap-2 text-[13px] font-semibold text-white underline decoration-gold decoration-2 underline-offset-[6px] transition-colors group-hover:text-gold">
          {linkLabel} <ArrowRight className="h-3.5 w-3.5" />
        </span>
      </div>
    </Link>
  );
}

function Collaboration() {
  const { t } = useLang();
  return (
    <section className="border-b border-ink/10" data-testid="b2b-teaser">
      <div className={WIDE}>
        <WideHeader kicker={t("nav_partners")} title={t("partners_title")} text={t("partners_sub")} testid="partners" />
        <div className="mt-10 grid gap-4 lg:grid-cols-3">
          <PartnerPanel testid="collab-b2b" image="/images/b2b.jpg" kicker="B2B" title={t("home_b2b_t")} text={t("home_b2b_p")[0]} href="/b2b" linkLabel={t("b2b_link1").replace(" ↗", "")} />
          <PartnerPanel testid="collab-architects" image="/images/arhitecti.jpg" kicker={t("home_arch_k")} title={t("home_arch_t")} text={t("home_arch_p")[0]} href="/arhitecti" linkLabel={t("b2b_link2").replace(" ↗", "")} />
          <PartnerPanel testid="collab-export" image="/images/structuri.jpg" kicker="Export" title={t("nav_export")} text={t("hdr_export_x")} href="/export" linkLabel={t("learn_more")} />
        </div>
      </div>
    </section>
  );
}

function Contact() {
  const { t } = useLang();
  return (
    <section className="bg-surface" data-testid="home-contact-section">
      <div className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8">
        <SectionTitle title={t("nav_contact")} />
        <div className="mt-10 grid gap-12 lg:grid-cols-[1fr_1.2fr]">
          <div>
            <p className="max-w-md text-[15px] leading-relaxed text-ink/65">
              {t("contact_text")}{" "}
              <a href={CONTACT.phoneHref} className="font-semibold text-ink underline decoration-gold decoration-2 underline-offset-4" data-testid="home-contact-phone">{CONTACT.phone}</a>.
            </p>
            <dl className="mt-8 grid gap-4 border-t border-ink/10 pt-6 text-[15px]">
              <div className="grid grid-cols-[120px_1fr] gap-4"><dt className="font-semibold text-ink/50">{t("addr")}</dt><dd className="text-ink/80">{CONTACT.address}</dd></div>
              <div className="grid grid-cols-[120px_1fr] gap-4"><dt className="font-semibold text-ink/50">Email</dt><dd className="text-ink/80">{CONTACT.email}</dd></div>
              {CONTACT.hours.map((h, i) => (
                <div key={h.days} className="grid grid-cols-[120px_1fr] gap-4"><dt className="font-semibold text-ink/50">{t("days")[i]}</dt><dd className="text-ink/80">{i === 2 ? t("closed") : h.time}</dd></div>
              ))}
            </dl>
          </div>
          <div className="border border-ink/10 bg-base p-6 sm:p-8">
            <ContactForm />
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <div data-testid="home-page">
      <SEO
        metaKey="home"
        title="HEAVY METAL — Prelucrări Metalice Buzău | Porți, Garduri, Structuri Metalice"
        description="Atelier de prelucrări metalice în Buzău, cu livrare națională: debitare laser, îndoire abkant CNC, sudură MIG/TIG/LASER, vopsitorie electrostatică. Porți, garduri, terase, carporturi și structuri metalice la comandă."
      />
      <Hero />
      <Services />
      <Stats />
      <Products />
      <Why />
      <Realizations />
      <Collaboration />
      <Contact />
    </div>
  );
}
